// pages/DataRefreshAdminPanel.js
// Admin → Data Refresh (spec §7): the shared snapshots every section reads from.
//   • Schedule — the forced refresh the Logic App's 5-minute tick applies
//     (dataIntegrity.snapshot_schedule).
//   • Datasets — age, rows, last run (status / duration / error), Refresh now, and
//     Query (view / test / edit the SQL behind it — DataRefreshQueryModal).
//     A run the row-count guard rejected offers "Accept smaller result".
//   • Run history — every build attempt, kept `retentionDays`.
// Backend: /api/diSnapshotAdmin. Reloads itself every 15 s while open.

import { useCallback, useEffect, useState } from 'react';
import ToggleSwitch from './sections/ToggleSwitch';
import DataRefreshQueryModal from './DataRefreshQueryModal';
import './DataRefreshAdminPanel.css';

const DAYS = [
  { key: 'mon', label: 'Mon' }, { key: 'tue', label: 'Tue' }, { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' }, { key: 'fri', label: 'Fri' }, { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];
const TIME_ZONES = ['America/Chicago', 'America/New_York', 'America/Denver', 'America/Los_Angeles', 'UTC'];
const RUN_STATUSES = ['ok', 'failed', 'rejected', 'kick_failed', 'skipped_locked'];

function age(ms) {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 120) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} hr`;
  return `${Math.round(h / 24)} days`;
}
const fmtDuration = (ms) => (ms == null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '—');
const fmtRows = (n) => (n == null ? '—' : Number(n).toLocaleString());

function StatusChip({ status }) {
  if (!status) return <span className="dr-chip dr-chip--none">never</span>;
  return <span className={`dr-chip dr-chip--${status}`}>{status.replace('_', ' ')}</span>;
}

async function api(method, body, qs = '') {
  const r = await fetch(`/api/diSnapshotAdmin${qs}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await r.text();
  let j = null;
  try { j = text ? JSON.parse(text) : null; } catch { /* non-JSON error */ }
  if (!r.ok) throw new Error((j && j.error) || text || `HTTP ${r.status}`);
  return j;
}

export default function DataRefreshAdminPanel({ flash }) {
  const [view, setView] = useState(null);          // { namespace, workerConfigured, datasets, schedule }
  const [err, setErr] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [draft, setDraft] = useState(null);        // schedule being edited
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [runs, setRuns] = useState(null);
  const [runFilter, setRunFilter] = useState({ dataset: '', status: '', days: 7 });
  const [queryFor, setQueryFor] = useState(null);  // dataset row whose query is open

  const load = useCallback(async () => {
    try { setView(await api('GET')); setErr(null); }
    catch (e) { setErr(e.message); }
  }, []);

  const loadRuns = useCallback(async () => {
    const qs = new URLSearchParams({ view: 'runs', days: String(runFilter.days) });
    if (runFilter.dataset) qs.set('dataset', runFilter.dataset);
    if (runFilter.status) qs.set('status', runFilter.status);
    try { setRuns((await api('GET', null, `?${qs}`)).runs || []); }
    catch (e) { setRuns([]); flash && flash(`Couldn't load run history: ${e.message}`, true); }
  }, [runFilter, flash]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) { load(); loadRuns(); } }, 15_000);
    return () => clearInterval(id);
  }, [load, loadRuns]);

  // Start editing from the saved schedule (and re-seed after a save).
  useEffect(() => {
    if (view && view.schedule && draft === null) setDraft({ ...view.schedule });
  }, [view, draft]);

  async function refreshNow(d, acceptDrop = false) {
    if (acceptDrop && !window.confirm(
      `Store the smaller ${d.label} result anyway?\n\nThe last build was rejected because its row count dropped sharply. `
      + 'Only accept it if you know the drop is real (e.g. a bulk close in PE).')) return;
    setBusyKey(d.key);
    try {
      const r = await api('POST', { action: 'refresh', dataset: d.name, params: d.params || undefined, acceptDrop });
      if (r.kicked) flash && flash(`Refresh started for ${d.label}. It shows here when it finishes.`);
      else flash && flash(`${d.label}: ${r.result.status}${r.result.error ? ` — ${r.result.error}` : ''}`, r.result.status !== 'ok');
      setTimeout(() => { load(); loadRuns(); }, 1500);
    } catch (e) {
      flash && flash(`Couldn't start the refresh: ${e.message}`, true);
    } finally {
      setBusyKey(null);
    }
  }

  async function saveSchedule() {
    setSavingSchedule(true);
    try {
      const r = await api('PUT', {
        enabled: draft.enabled,
        intervalMin: Number(draft.intervalMin),
        startLocal: draft.startLocal,
        endLocal: draft.endLocal,
        days: draft.days,
        timeZone: draft.timeZone,
        retentionDays: Number(draft.retentionDays),
      });
      setView(v => ({ ...v, schedule: r.schedule }));
      setDraft({ ...r.schedule });
      flash && flash('Refresh schedule saved.');
    } catch (e) {
      flash && flash(`Couldn't save the schedule: ${e.message}`, true);
    } finally {
      setSavingSchedule(false);
    }
  }

  if (!view) {
    return (
      <div className="admin-panel">
        <h3>Data Refresh</h3>
        {err ? <div className="admin-flash error">Couldn't load ({err}).</div> : <p className="admin-hint">Loading…</p>}
      </div>
    );
  }

  const sched = view.schedule || {};
  const dirty = draft && JSON.stringify({ ...draft, nextRunAt: null, lastScheduledAt: null, updatedAt: null, updatedBy: null })
    !== JSON.stringify({ ...sched, nextRunAt: null, lastScheduledAt: null, updatedAt: null, updatedBy: null });
  const datasetNames = [...new Set(view.datasets.map(d => d.name))];

  return (
    <div className="admin-panel dr-panel">
      <h3>Data Refresh</h3>
      <p className="admin-hint">
        Each source below is queried once and shared by every user. A copy older than its refresh
        interval is served immediately and rebuilt in the background; sections that aren’t visible in an
        active period aren’t refreshed. Sign-offs and edits are always live.
      </p>

      {!view.workerConfigured && (
        <div className="admin-flash error">
          The refresh worker isn’t configured (DI_SNAPSHOT_WORKER_URL / DI_SNAPSHOT_WORKER_KEY), so
          refreshes run inside the request that needed them.
          {view.namespace === 'local' ? ' Expected in local dev.' : ' Set both app settings in production.'}
        </div>
      )}
      {err && <div className="admin-flash error">Last reload failed ({err}) — showing the previous status.</div>}

      {/* ── Schedule ─────────────────────────────────────────────────────── */}
      <h4>Scheduled refresh</h4>
      {sched.missing ? (
        <div className="admin-flash error">The schedule table isn’t available{sched.error ? ` (${sched.error})` : ''} — apply sql/035.</div>
      ) : draft && (
        <div className="dr-schedule">
          <p className="admin-hint">
            Every 5 minutes a Logic App calls the tick; inside this window each tick that falls on the interval rebuilds
            every active source, fresh or not, so data stays well inside its 10-minute TTL and nobody has to trigger
            a refresh by opening a page.
          </p>
          <div className="dr-form">
            <label className="dr-field">
              <span>Enabled</span>
              <ToggleSwitch checked={!!draft.enabled} onChange={v => setDraft({ ...draft, enabled: v })} label={draft.enabled ? 'On' : 'Off'} />
            </label>
            <label className="dr-field">
              <span>Every</span>
              <span><input className="admin-input dr-small" type="number" min={5} max={1440} value={draft.intervalMin}
                onChange={e => setDraft({ ...draft, intervalMin: e.target.value })} /> min</span>
            </label>
            <label className="dr-field">
              <span>From</span>
              <input className="admin-input dr-small" type="time" value={draft.startLocal}
                onChange={e => setDraft({ ...draft, startLocal: e.target.value })} />
            </label>
            <label className="dr-field">
              <span>Until</span>
              <input className="admin-input dr-small" type="time" value={draft.endLocal}
                onChange={e => setDraft({ ...draft, endLocal: e.target.value })} />
            </label>
            <label className="dr-field">
              <span>Time zone</span>
              <select className="admin-input" value={draft.timeZone} onChange={e => setDraft({ ...draft, timeZone: e.target.value })}>
                {[...new Set([draft.timeZone, ...TIME_ZONES])].map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </label>
            <label className="dr-field">
              <span>Keep history</span>
              <span><input className="admin-input dr-small" type="number" min={1} max={365} value={draft.retentionDays}
                onChange={e => setDraft({ ...draft, retentionDays: e.target.value })} /> days</span>
            </label>
          </div>
          <div className="dr-days">
            {DAYS.map(d => (
              <label key={d.key} className="admin-gate-chip">
                <input type="checkbox" checked={draft.days.includes(d.key)}
                  onChange={e => setDraft({
                    ...draft,
                    days: e.target.checked ? [...draft.days, d.key] : draft.days.filter(x => x !== d.key),
                  })} />
                {d.label}
              </label>
            ))}
          </div>
          <div className="dr-schedule-foot">
            <span className="dr-sub">
              Next run: {sched.enabled ? fmtTime(sched.nextRunAt) : 'off'} · Last: {fmtTime(sched.lastScheduledAt)}
              {sched.updatedBy && ` · Changed by ${sched.updatedBy} ${fmtTime(sched.updatedAt)}`}
            </span>
            <span>
              {dirty && <button className="admin-btn admin-btn--secondary" onClick={() => setDraft({ ...sched })}>Discard</button>}
              {' '}
              <button className="admin-btn" disabled={!dirty || savingSchedule} onClick={saveSchedule}>
                {savingSchedule ? 'Saving…' : 'Save schedule'}
              </button>
            </span>
          </div>
        </div>
      )}

      {/* ── Datasets ─────────────────────────────────────────────────────── */}
      <h4>Datasets <span className="dr-namespace" title="Blob namespace">{view.namespace}</span></h4>
      <table className="admin-table dr-table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Age</th>
            <th className="dr-num">Rows</th>
            <th>Last run</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {view.datasets.map(d => {
            const lr = d.lastRun;
            const stale = d.ageMs != null && d.ageMs > d.ttlMs;
            const rejectedDrop = lr && lr.status === 'rejected' && /row count fell/.test(lr.error || '');
            return (
              <tr key={d.key} className={!d.active ? 'dr-row--inactive' : undefined}>
                <td>
                  <div className="dr-label">{d.label}{d.params ? <span className="dr-variant"> {Object.values(d.params).join(' · ')}</span> : null}</div>
                  <div className="dr-tags">
                    {d.global
                      ? <span className="dr-tag">global</span>
                      : (d.sections || []).map(s => <span key={s} className="dr-tag">{s}</span>)}
                    {!d.active && <span className="dr-tag dr-tag--off">not refreshed (inactive)</span>}
                    {d.queryOverride && (
                      <span className="dr-tag dr-tag--edited" title={`Edited by ${d.queryOverride.by} ${fmtTime(d.queryOverride.at)}`}>edited query</span>
                    )}
                    {d.isMock && <span className="dr-tag dr-tag--off">mock (local only)</span>}
                  </div>
                </td>
                <td className={stale && d.active ? 'dr-stale' : undefined} title={d.builtAt ? `Built ${fmtTime(d.builtAt)} · refresh after ${age(d.ttlMs)}` : 'Never built'}>
                  {d.exists ? age(d.ageMs) : 'none'}
                  {d.refreshing && <i className="fas fa-sync-alt fa-spin dr-spin" title="Refreshing" />}
                </td>
                <td className="dr-num">{fmtRows(d.rowCount)}</td>
                <td>
                  <StatusChip status={lr && lr.status} />
                  {lr && <span className="dr-sub">{fmtDuration(lr.durationMs)} · {lr.trigger} · {fmtTime(lr.startedAt)}</span>}
                  {lr && lr.error && <div className="dr-error" title={lr.error}>{lr.error}</div>}
                </td>
                <td className="dr-actions">
                  {d.editable && (
                    <button className="admin-btn admin-btn--secondary dr-querybtn" onClick={() => setQueryFor(d)} title="View or edit the SQL behind this dataset">
                      Query
                    </button>
                  )}
                  <button className="admin-btn admin-btn--secondary" disabled={busyKey === d.key} onClick={() => refreshNow(d)}>
                    {busyKey === d.key ? 'Starting…' : 'Refresh now'}
                  </button>
                  {rejectedDrop && (
                    <button className="dr-linkbtn" disabled={busyKey === d.key} onClick={() => refreshNow(d, true)}>
                      Accept smaller result
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ── Run history ──────────────────────────────────────────────────── */}
      <h4>Run history</h4>
      <div className="dr-filters">
        <select className="admin-input" value={runFilter.dataset} onChange={e => setRunFilter({ ...runFilter, dataset: e.target.value })}>
          <option value="">All sources</option>
          {datasetNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select className="admin-input" value={runFilter.status} onChange={e => setRunFilter({ ...runFilter, status: e.target.value })}>
          <option value="">All results</option>
          {RUN_STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
        <select className="admin-input" value={runFilter.days} onChange={e => setRunFilter({ ...runFilter, days: Number(e.target.value) })}>
          {[1, 7, 30].map(n => <option key={n} value={n}>Last {n} day{n === 1 ? '' : 's'}</option>)}
        </select>
      </div>
      {runs === null ? <p className="admin-hint">Loading…</p> : runs.length === 0 ? (
        <p className="admin-hint">No runs in this window.</p>
      ) : (
        <div className="dr-runs">
          <table className="admin-table dr-table">
            <thead>
              <tr><th>Started</th><th>Source</th><th>Trigger</th><th>Result</th><th className="dr-num">Time</th><th className="dr-num">Rows</th></tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id}>
                  <td className="dr-nowrap">{fmtTime(r.started_at)}</td>
                  <td>{r.variant_key}</td>
                  <td>{r.trigger_type}{r.triggered_by ? <div className="dr-sub">{r.triggered_by}</div> : null}</td>
                  <td>
                    <StatusChip status={r.status} />
                    {(r.error || r.note) && <div className={r.error ? 'dr-error' : 'dr-sub'} title={r.error || r.note}>{r.error || r.note}</div>}
                  </td>
                  <td className="dr-num">{fmtDuration(r.duration_ms)}</td>
                  <td className="dr-num">
                    {r.prev_row_count != null && r.row_count != null && r.prev_row_count !== r.row_count
                      ? <>{fmtRows(r.prev_row_count)} → {fmtRows(r.row_count)}</>
                      : fmtRows(r.row_count)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {queryFor && (
        <DataRefreshQueryModal
          dataset={queryFor.name}
          label={queryFor.label}
          flash={flash}
          onClose={() => setQueryFor(null)}
          onSaved={() => { load(); loadRuns(); }}
        />
      )}
    </div>
  );
}
